import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Gavel, ChevronRight, ChevronLeft, Check, MapPin, Lock, Database } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

const STEPS = ["Dati generali", "Regione & Prezzario", "Riepilogo"];

export default function NewOffer() {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedRegion = new URLSearchParams(location.search).get("region") || "";
  const [step, setStep] = useState(0);
  const [regions, setRegions] = useState([]);
  const [summary, setSummary] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", customer_id: "", client_name: "", client_type: "privato", region: "",
    station: "", location: "", description: "", base_amount: 0, ribasso: 0, security_charges: 0,
  });

  useEffect(() => {
    api.regions().then(data => { setRegions(data); if (requestedRegion && data.includes(requestedRegion)) setForm(f => ({...f, region: requestedRegion})); }).catch(() => {});
    api.prezzariSummary().then(setSummary).catch(() => {});
    api.listCustomers().then(setCustomers).catch(() => {});
  }, [requestedRegion]);

  const infoFor = (r) => summary.find((s) => s.region === r);
  const available = regions.filter(infoFor);
  const unavailable = regions.filter((r) => !infoFor(r));
  const selInfo = infoFor(form.region);
  const prezzarioLabel = form.region ? `${form.region}${selInfo?.year ? ` ${selInfo.year}` : ""}` : "—";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 0) return form.title.trim().length > 0 && Boolean(form.customer_id);
    if (step === 1) return form.region.length > 0;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const offer = await api.createOffer(form);
      toast.success("Offerta creata");
      navigate(`/offerte/${offer.id}`);
    } catch (e) {
      toast.error("Errore nella creazione");
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-3xl font-display font-extrabold tracking-tight text-slate-900">Nuova Offerta</h1>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-orange-600 text-white" : "bg-slate-200 text-slate-500"}`}>
              {i < step ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-sm font-medium hidden sm:block ${i === step ? "text-slate-900" : "text-slate-400"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 rounded ${i < step ? "bg-emerald-400" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 space-y-6 fade-up">
        {step === 0 && (
          <>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Titolo offerta *</label>
              <input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="offer-title-input" placeholder="Es. Ristrutturazione edificio residenziale Via Roma"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Tipo di offerta</label>
              <div className="grid grid-cols-2 gap-3">
                {[["privato", "Privato", Building2], ["gara", "Gara d'Appalto", Gavel]].map(([val, lab, Icon]) => (
                  <button key={val} onClick={() => set("client_type", val)} data-testid={`offer-type-${val}`}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${form.client_type === val ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <Icon className={`w-5 h-5 ${form.client_type === val ? "text-orange-600" : "text-slate-400"}`} />
                    <span className={`text-sm font-semibold ${form.client_type === val ? "text-orange-700" : "text-slate-600"}`}>{lab}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{form.client_type === "gara" ? "Stazione appaltante" : "Committente"}</label>
                <select value={form.customer_id} onChange={(e) => { const c=customers.find(x=>x.id===e.target.value); setForm(f=>({...f,customer_id:e.target.value,client_name:c?.name||""})); }} data-testid="offer-client-select"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500">
                  <option value="">Seleziona cliente…</option>
                  {customers.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div className="mt-1 text-[11px] text-slate-400">Gestisci l'anagrafica completa dalla sezione Clienti.</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Località cantiere</label>
                <input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="offer-location-input"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Descrizione</label>
              <textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="offer-description-input" rows={3}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500" />
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Regione (prezzario ufficiale) *</label>
              {available.length === 0 ? (
                <div className="border border-dashed border-amber-300 bg-amber-50 rounded-lg p-4 text-sm text-amber-800" data-testid="no-prezzari-warning">
                  <p className="font-semibold flex items-center gap-2"><Database className="w-4 h-4" /> Nessun prezzario caricato</p>
                  <p className="mt-1">Per creare un'offerta devi prima importare almeno un prezzario regionale.</p>
                  <Link to="/prezzari" data-testid="go-to-prezzari-link" className="inline-block mt-2 font-semibold underline">Vai a Prezzari → Importa</Link>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
                  {available.map((r) => {
                    const info = infoFor(r);
                    return (
                      <button key={r} onClick={() => set("region", r)} data-testid={`region-option-${r}`}
                        className={`flex items-start gap-2 p-3 rounded-lg border text-sm text-left transition-all ${form.region === r ? "border-orange-500 bg-orange-50 text-orange-700 font-semibold" : "border-slate-200 hover:border-slate-300 text-slate-600"}`}>
                        <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                        <span className="min-w-0">
                          <span className="block">{r}</span>
                          <span className="block text-[11px] font-normal text-slate-400" data-testid={`region-info-${r}`}>Prezzario {info.year || "—"} · {info.count.toLocaleString("it-IT")} voci</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {unavailable.length > 0 && available.length > 0 && (
                <details className="mt-3" data-testid="unavailable-regions">
                  <summary className="text-xs text-slate-400 cursor-pointer select-none">{unavailable.length} regioni senza prezzario caricato</summary>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {unavailable.map((r) => (
                      <span key={r} data-testid={`region-disabled-${r}`} title="Nessun prezzario caricato per questa regione" className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-100 bg-slate-50 text-[11px] text-slate-400 cursor-not-allowed"><Lock className="w-3 h-3" /> {r}</span>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">Importa il prezzario dalla sezione <Link to="/prezzari" className="underline">Prezzari</Link> per abilitare una regione.</p>
                </details>
              )}
              {form.region && <p className="text-xs text-slate-500 mt-3" data-testid="selected-prezzario-label">Prezzario ufficiale <span className="font-semibold text-slate-700">{prezzarioLabel}</span> selezionato ({selInfo?.count?.toLocaleString("it-IT")} voci). I prezzi mancanti verranno cercati automaticamente qui.</p>}
            </div>
            {form.client_type === "gara" && (
              <div className="grid sm:grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Importo a base d'asta (€)</label>
                  <input type="number" value={form.base_amount} onChange={(e) => set("base_amount", parseFloat(e.target.value) || 0)} data-testid="offer-base-amount-input"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Ribasso d'asta (%)</label>
                  <input type="number" value={form.ribasso} onChange={(e) => set("ribasso", parseFloat(e.target.value) || 0)} data-testid="offer-ribasso-input"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500" />
                </div>
              </div>
            )}
          </>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <h3 className="font-display font-semibold text-lg">Riepilogo</h3>
            {[
              ["Titolo", form.title],
              ["Tipo", form.client_type === "gara" ? "Gara d'Appalto" : "Privato"],
              [form.client_type === "gara" ? "Stazione appaltante" : "Committente", form.client_name || "—"],
              ["Località", form.location || "—"],
              ["Regione / Prezzario", prezzarioLabel],
              ...(form.client_type === "gara" ? [["Ribasso d'asta", `${form.ribasso}%`]] : []),
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-slate-100 py-2 text-sm">
                <span className="text-slate-500">{k}</span>
                <span className="font-medium text-slate-900 text-right">{v}</span>
              </div>
            ))}
            <p className="text-xs text-slate-500 pt-2">Dopo la creazione potrai caricare i file di progetto, avviare l'analisi AI e comporre il CME.</p>
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button onClick={() => (step === 0 ? navigate("/offerte") : setStep(step - 1))} data-testid="wizard-back-button"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50">
          <ChevronLeft className="w-4 h-4" /> {step === 0 ? "Annulla" : "Indietro"}
        </button>
        {step < STEPS.length - 1 ? (
          <button onClick={() => canNext() ? setStep(step + 1) : toast.error("Completa i campi obbligatori")} data-testid="wizard-next-button"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all ${canNext() ? "bg-orange-600 hover:bg-orange-700 active:scale-95" : "bg-slate-300 cursor-not-allowed"}`}>
            Avanti <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={saving} data-testid="wizard-submit-button"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-orange-600 hover:bg-orange-700 active:scale-95 disabled:opacity-60">
            <Check className="w-4 h-4" /> {saving ? "Creazione…" : "Crea Offerta"}
          </button>
        )}
      </div>
    </div>
  );
}
